import type { AutocompleteChangeReason } from '@mui/material';

import * as React from 'react';
import { useSearchParams } from 'react-router';

export function useFilterAutocomplete<
  TDefault extends string | number,
  TData extends TDefault | { id: TDefault },
>(options: {
  searchParamKey: string;
  defaultValue: TDefault[];
  data: Readonly<TData[]> | TData[] | undefined;
}) {
  const [, setSearchParams] = useSearchParams();

  // Cache the initial value to prevent further updates from re-running the
  // effect below.
  const [initialValue] = React.useState(() => options.defaultValue);

  // For some filters, the data is fetched asynchronously so we make the
  // assumption that if it's available on the first render, it has been
  // initialized. This way, we avoid synchronizing the data in the effect below.
  const [hasDataInitialized, setHasDataInitialized] = React.useState(
    () => (options.data ?? []).length > 0
  );

  const [value, setValue] = React.useState<TData[]>(() =>
    initialValue.length > 0
      ? (options.data ?? []).filter((item) =>
          initialValue.includes(typeof item === 'object' ? item.id : (item as TDefault))
        )
      : []
  );

  // Synchronize the data with the initial values once the data is available.
  React.useEffect(() => {
    if (!hasDataInitialized && options.data && initialValue.length > 0) {
      setValue(
        options.data.filter((item) =>
          initialValue.includes(typeof item === 'object' ? item.id : (item as TDefault))
        )
      );
      setHasDataInitialized(true);
    }
  }, [hasDataInitialized, options.data, initialValue]);

  function onChange(_event: unknown, values: TData[], reason: AutocompleteChangeReason) {
    setValue(values);
    // The only way to select an option is by having the dropdown open, so we
    // wait for it to be closed before updating the search params.
    if (reason !== 'selectOption') {
      updateSearchParams(values);
    }
  }

  function onClose() {
    updateSearchParams(value);
  }

  function updateSearchParams(value: TData[]) {
    setSearchParams((searchParams) => {
      searchParams.delete(options.searchParamKey);
      if (value.length > 0) {
        for (const v of value) {
          searchParams.append(
            options.searchParamKey,
            typeof v === 'object' ? String(v.id) : String(v)
          );
        }
      }
      return searchParams;
    });
  }

  function getAutocompleteProps() {
    return {
      multiple: true as const,
      disableCloseOnSelect: true,
      limitTags: 1,
      inputSize: 'medium' as const,
      value,
      onChange,
      onClose,
    };
  }

  return { getAutocompleteProps };
}
